import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLoad, act } from "@/lib/hooks";
import { api, fmt, fdate, fdatetime } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge } from "@/components/common";
import { ActivityDialog } from "@/components/crm/ActivityDialog";
import { ActivityTimeline } from "@/components/crm/ActivityTimeline";
import { LeadForm } from "@/components/crm/LeadForm";

const SALES = ["tenant_admin", "accountant", "sales"];

function Info({ rows }) {
  return <dl className="grid gap-x-6 gap-y-3 p-5 text-sm sm:grid-cols-2">{rows.map(([k, v, id]) => <div key={k}><dt className="po-label">{k}</dt><dd className="mt-0.5 text-slate-900" data-testid={id}>{v || "—"}</dd></div>)}</dl>;
}

function Notes({ lid, notes, reload, can }) {
  const [t, setT] = useState("");
  const add = async () => { if (await act(() => api.post(`/crm/leads/${lid}/notes`, { text: t }), "Note added")) { setT(""); reload(); } };
  return (
    <Section title="Notes" testId="lead-notes-section">
      {can && <div className="flex gap-2 border-b border-slate-100 p-4"><Textarea data-testid="lead-note-new" rows={2} value={t} onChange={(e) => setT(e.target.value)} placeholder="Add a note…" />
        <Button data-testid="lead-note-add" disabled={!t.trim()} onClick={add} className="self-end">Add</Button></div>}
      <ul className="divide-y divide-slate-100" data-testid="lead-notes-history">
        {notes.length === 0 && <li className="px-5 py-4 text-sm text-slate-400">No notes yet.</li>}
        {notes.map((n) => <li key={n.id} className="px-5 py-3 text-sm"><p className="whitespace-pre-wrap text-slate-800">{n.text}</p><div className="mt-1 text-xs text-slate-400">{n.by} · {fdatetime(n.at)}</div></li>)}
      </ul>
    </Section>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const [d, reload] = useLoad(`/crm/leads/${id}`);
  const [act_, setAct] = useState(false);
  const [edit, setEdit] = useState(null);
  const { canWrite, currency } = useAccess();
  const can = canWrite(SALES);
  if (!d) return null;
  const l = d.lead;
  return (
    <>
      <Link to="/app/crm" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="lead-back"><ArrowLeft className="h-4 w-4" />CRM</Link>
      <PageHeader title={l.name} subtitle={`${l.company || "No company"} · score ${l.score} · created ${fdate(l.created_at)} by ${l.created_by}`}>
        <StatusBadge status={l.status === "qualified" ? "active" : l.status === "disqualified" ? "canceled" : "pending"} label={l.status} testId="lead-detail-status" className="self-center" />
        {can && l.status !== "qualified" && <Button variant="outline" data-testid="lead-detail-edit" onClick={() => setEdit(l)}><Pencil className="mr-1 h-4 w-4" />Edit</Button>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Lead details" testId="lead-info"><Info rows={[["Email", l.email, "lead-info-email"], ["Phone", l.phone], ["Source", l.source], ["Assigned to", d.assignee?.name, "lead-info-assigned"],
            ["Estimated deal value", l.estimated_value != null ? fmt(l.estimated_value, currency) : null, "lead-info-value"], ["Expected close", l.expected_close && fdate(l.expected_close), "lead-info-close"],
            ["Product / service interest", l.service_interest, "lead-info-interest"], ["Branch", d.branch?.name, "lead-info-branch"],
            ["Tags", l.tags?.length ? l.tags.join(", ") : null, "lead-info-tags"], ["Current notes", l.notes]]} /></Section>
          <Section title="Activity timeline" testId="lead-activities" action={can && <Button size="sm" data-testid="lead-log-activity" onClick={() => setAct(true)}><Plus className="mr-1 h-4 w-4" />Log activity</Button>}>
            <ActivityTimeline rows={d.activities} reload={reload} can={can} testId="lead-activity-timeline" />
          </Section>
        </div>
        <div className="space-y-6">
          <Section title="Opportunity" testId="lead-opportunity-card">
            {d.opportunity ? <Link to={`/app/crm/opportunities/${d.opportunity.id}`} data-testid="lead-opportunity-link" className="block space-y-1 p-5 hover:bg-slate-50">
              <div className="font-medium text-slate-900">{d.opportunity.title}</div>
              <div className="text-sm text-slate-500">Stage {d.opportunity.stage} · {fmt(d.opportunity.value, currency)}</div></Link>
              : <p className="p-5 text-sm text-slate-400">Qualify this lead to create an opportunity.</p>}
          </Section>
          <Section title="Customer" testId="lead-customer-card">
            {d.customer ? <Link to={`/app/customers/${d.customer.id}`} data-testid="lead-customer-link" className="block p-5 font-medium text-[#13294B] hover:bg-slate-50">{d.customer.name}</Link>
              : <p className="p-5 text-sm text-slate-400">Not yet a customer.</p>}
          </Section>
          <Notes lid={id} notes={d.notes_history} reload={reload} can={can} />
        </div>
      </div>
      <ActivityDialog open={act_} onClose={() => setAct(false)} onSaved={reload} target={{ lead_id: id }} />
      <LeadForm lead={edit} onClose={() => setEdit(null)} onSaved={reload} />
    </>
  );
}
